import { createStore, StoreOptions } from 'vuex';
import { RootState } from './types';
import axios from 'axios';
import { msalPluginInstance } from "@/plugins/msal";
import createPersistedState from 'vuex-persistedstate';
// @ts-ignore
import Cookies from 'js-cookie';
import { main } from './main/index';
import { admin } from './admin/index';

// @ts-ignore
const browserLang = navigator.language || navigator.userLanguage;

const defaultCulture = browserLang.startsWith('nl') ? 'nl' : 'en';

const store: StoreOptions<RootState> = {
    state: {
        lastUserActionDateTime: Date.now(),
        error: null,
        debug: false,
        busy: 0,
        inProgress: 0,
        user: null,
        databaseStatus: 0,
        fileFormatCulture: defaultCulture,
        exportFormat: 'xlsx',
        isLoggedIn: false,
        bearerToken: '',
        environment: '',
        version: '',
        notificationText: null,
        streetViewKey: ''
    },
    modules: {
        main,
        admin
    },
    plugins: [createPersistedState({
        paths: ['fileFormatCulture', 'exportFormat'],
        storage: {
            getItem: key => Cookies.get(key),
            setItem: (key: any, value: any ) => Cookies.set(key, value, { expires: 3, secure: true }),
            removeItem: key => Cookies.remove(key)
        }
    })],
    getters: {
        getBusy(state: RootState) {
            return state.busy;
        },
        getDebug(state: RootState) {
            return state.debug;
        },
        getInProgress(state: RootState) {
            return state.inProgress;
        },
        getDatabaseStatus(state: RootState) {
            return state.databaseStatus;
        },
        getError(state: RootState) {
            return state.error;
        },
        getUser(state: RootState) {
            return state.user;
        },
        getLastUserActionDateTime(state: RootState) {
            return state.lastUserActionDateTime;
        },
        getFileFormatCulture(state: RootState) {
            return state.fileFormatCulture;
        },
        getExportFormat(state: RootState) {
            return state.exportFormat;
        },
        getIsLoggedIn(state: RootState) {
            return state.isLoggedIn;
        },
        getBearerToken(state: RootState) {
            return state.bearerToken;
        },
        getEnvironment(state: RootState) {
            return state.environment;
        },
        getVersion(state: RootState) {
            return state.version;
        },
        getNotificationText(state: RootState) {
            return state.notificationText;
        },
        getStreetViewKey(state: RootState) {
            return state.streetViewKey;
        },
        
    },
    mutations: {
        SET_VERSION(state, version: string) {
            state.version = version
        },
        SET_DEBUG(state, debug: boolean) {
            state.debug = debug
        },
        SET_DATABASESTATUS(state, databaseStatus: number) {
            state.databaseStatus = databaseStatus
        },
        SET_ERROR(state: RootState, payload: any) {
            // Always clear the busy / in-progress counters — both when an error is raised and
            // when it is dismissed. A model load fires several concurrent calls; if one fails
            // (e.g. a 402 usage limit) the others can still increment the counters afterwards.
            // Without resetting on dismiss, the leftover busy count keeps the blocking overlay
            // up and the logo spinning, locking the UI after the user closes the dialog.
            state.busy = 0;
            state.inProgress = 0;
            if (payload != null) {
                // A 402 is an expected "usage limit reached" situation, not a fault — log it
                // calmly so it doesn't show up as a red console error.
                if (payload?.response?.status === 402) {
                    console.info('Usage limit reached', payload?.response?.data);
                } else {
                    console.error(payload);
                }
            }
            state.error = payload;
        },
        SET_FILEFORMATCULTURE(state: RootState, payload: string) {
            state.fileFormatCulture = payload;
        },
        SET_EXPORTFORMAT(state: RootState, payload: string) {
            state.exportFormat = payload;
        },
        SET_USER(state: RootState, payload: any) {
            state.user = payload;
        },

        SET_STARTBUSY(state: RootState) {
            if (state.busy == null) {
                state.busy = 0;
            }
            state.busy++;
            
            
        },
        SET_STARTINPROGRESS(state: RootState) {
            if (state.inProgress == null) {
                state.inProgress = 0;
            }
            state.inProgress++;
            
        },
        SET_ENDBUSY(state: RootState) {
            if (state.busy == null) {
                state.busy = 0;
            }
            else {
                state.busy--;
            }
            if (state.busy < 0) state.busy = 0;
            state.lastUserActionDateTime = Date.now();
            
            
        },
        SET_ENDINPROGRESS(state: RootState) {
            if (state.inProgress == null) {
                state.inProgress = 0;
            }
            else {
                state.inProgress--;
            }
            if (state.inProgress < 0) state.inProgress = 0;
            state.lastUserActionDateTime = Date.now();
            
            
        },
        SET_NOTIFICATIONTEXT(state: RootState, text: string) {
            state.notificationText = text == null ? null : text;
            setTimeout(() => {
                state.notificationText = null;
            }, 10000); 
        }
        
        
    },
    actions: {
        async loggedIn({ commit, dispatch }): Promise<any> {
            try {
                await dispatch('waitForDatabase')
                const _self = this;
                axios.interceptors.request.use(async function (config: any) {
                    const token = await msalPluginInstance.acquireToken();
                    if (token != null) {
                        config.headers.Authorization = `Bearer ${token}`;
                        config.headers.Accept = 'application/json';
                        _self.state.bearerToken = token as string;
                    }
                    
                    return config;
                }, function (err) {
                    return Promise.reject(err);
                });

                axios.get('/api/system/current-user').then(response => {
                    this.state.user = response.data;
                });
                
                this.state.isLoggedIn = true;
            } finally {
            }
        },

        async downloadCsv(context: any, file: any) {
            context.commit('SET_STARTBUSY', null, { root: true });
            return axios.get(file.url).then(async response => {
                //only add the BOM if the content is not empty.
                const blob = response.data == '' ? new Blob([response.data], { type: 'application/pdf' }) : new Blob(['\ufeff' + response.data], { type: 'text/csv' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = file.name;
                link.click();
                URL.revokeObjectURL(link.href);
                context.commit('SET_ENDBUSY', null, { root: true });
            }).catch((error) => {
                context.commit('SET_ERROR', error, { root: true })
            })
        },


      

        async downloadFile(context: any, file: any) {
            
            context.commit('SET_STARTBUSY', null, { root: true });
            return axios.get(file.url, {
                responseType: 'arraybuffer',
                headers: {
                    'Accept': 'application/pdf'
                }
            }).then(async response => {
                const link = document.createElement('a');
                link.href = URL.createObjectURL(new Blob([response.data]));
                link.download = file.name;
                link.click();
                URL.revokeObjectURL(link.href);
                context.commit('SET_ENDBUSY', null, { root: true });
            }).catch((error) => {
                context.commit('SET_ERROR', error, { root: true })
            })
        },

        async waitForDatabase(context: any) {
            context.commit('SET_DATABASESTATUS', 1);
            
            axios.get('/api/system/environment').then(response => {
                this.state.environment = response.data;
            });
            axios.get('/api/system/version').then(response => {
                this.state.version = response.data;
            });
            axios.get('/api/system/street-view-key').then(response => {
                this.state.streetViewKey = response.data;
            });
            return axios.get('/api/system/status').then(response => {
                if (response.data == true) {
                    
                    
                        
                    return;
                }
                setTimeout(function () {

                    context.dispatch('waitForDatabase')
                }, 1000)

            }).catch(() => { //error
                // console.error(error);
                setTimeout(function () {

                    context.dispatch('waitForDatabase')
                }, 1000)


            })

        },
    }
};

export default createStore(store);


